import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import api from '../api';
import { formatNumber, formatRelativeTime } from '../utils/formatters';

function toneClasses(value = 'healthy') {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'critical' || normalized === 'expired' || normalized === 'offline') {
    return 'border-[#93000a]/60 bg-[#2a1112] text-[#ffdad6]';
  }
  if (normalized === 'warning' || normalized === 'pending' || normalized === 'trial') {
    return 'border-[#fd6c00]/40 bg-[#2b1403] text-[#ffb692]';
  }
  return 'border-[#f1c100]/20 bg-[#14120a] text-[#f7d88a]';
}

export default function ProductDesk() {
  const { productKey = '' } = useParams();
  const [desk, setDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadDesk = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get(`/admin/platform/product-desks/${productKey}`);
      setDesk(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load this product desk.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [productKey]);

  useEffect(() => {
    setLoading(true);
    loadDesk();
  }, [loadDesk]);

  const runAction = async (action) => {
    if (!action) return;
    if (action.href) {
      window.open(action.href, '_blank', 'noopener,noreferrer');
      return;
    }

    const requiresConfirm = action.requiresConfirm === true;
    if (
      requiresConfirm &&
      !window.confirm(`Run "${action.label}" on ${desk?.source?.productLabel || productKey}?`)
    ) {
      return;
    }

    setBusyAction(action.key || action.action);
    setError('');
    setMessage('');
    try {
      const { data } = await api.post('/admin/platform/command-actions', {
        productKey: action.productKey || productKey,
        action: action.action,
        payload: action.payload || {},
        confirm: requiresConfirm,
      });
      setMessage(data.message || 'Command action completed.');
      setRefreshing(true);
      await loadDesk();
    } catch (err) {
      const response = err?.response?.data;
      if (response?.requiresConfirm) {
        const confirmed = window.confirm(response.message || 'This action needs confirmation.');
        if (confirmed) {
          try {
            const { data } = await api.post('/admin/platform/command-actions', {
              productKey: action.productKey || productKey,
              action: action.action,
              payload: action.payload || {},
              confirm: true,
            });
            setMessage(data.message || 'Command action completed.');
            setRefreshing(true);
            await loadDesk();
          } catch (retryErr) {
            setError(retryErr?.response?.data?.message || 'Could not run this command action.');
          }
        }
      } else {
        setError(response?.message || 'Could not run this command action.');
      }
    } finally {
      setBusyAction('');
      setRefreshing(false);
    }
  };

  const snapshot = desk?.snapshot || null;
  const source = desk?.source || null;
  const alerts = desk?.alerts || [];
  const quickActions = useMemo(() => desk?.actions || [], [desk?.actions]);

  if (loading) {
    return (
      <Layout>
        <StatePanel message="Loading product desk..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2rem] border border-[#4e4632] bg-[radial-gradient(circle_at_top_left,rgba(241,193,0,0.16),transparent_35%),linear-gradient(180deg,#111111,#050505)] p-6 shadow-[0_22px_60px_rgba(0,0,0,0.36)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#f1c100]">
                Product Control Module
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                {source?.productLabel || snapshot?.productLabel || productKey}
              </h1>
              <p className="mt-3 text-sm leading-7 text-[#d2c5ab]">
                This desk turns the live analytics feed into a focused product workspace with alerts,
                renewals, queues, source links, and command actions.
              </p>
            </div>

            <button
              type="button"
              onClick={() => runAction({ key: 'refresh', action: 'refresh_analytics', productKey })}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-full bg-[#ffcc00] px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] text-[#3d2f00] transition hover:brightness-110 disabled:opacity-70"
            >
              <MaterialIcon name={refreshing ? 'sync' : 'refresh'} className={refreshing ? 'animate-spin text-sm' : 'text-sm'} />
              {refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Status" value={source?.status || 'pending'} accent="text-white" />
            <SummaryCard label="License" value={source?.licenseStatus || 'unknown'} accent="text-[#f7d88a]" />
            <SummaryCard label="Open alerts" value={alerts.length} accent="text-[#ffb692]" />
            <SummaryCard
              label="Last snapshot"
              value={source?.capturedAt ? formatRelativeTime(source.capturedAt) : 'none'}
              accent="text-[#f1c100]"
            />
          </div>
        </section>

        {message ? <StatePanel message={message} tone="success" /> : null}
        {error ? <StatePanel message={error} tone="error" /> : null}

        <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="space-y-6">
            <Panel title="Telemetry cards">
              <div className="grid gap-4 md:grid-cols-2">
                {(snapshot?.cards || []).map((card) => (
                  <div key={card.key} className="rounded-[1.2rem] border border-[#4e4632] bg-[#111111] p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9a9078]">
                      {card.label}
                    </p>
                    <p className="mt-3 text-2xl font-black text-white">
                      {typeof card.value === 'number' ? formatNumber(card.value) : card.value}
                    </p>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Queues and pressure">
              <div className="grid gap-4 md:grid-cols-2">
                {(snapshot?.queues || []).map((queue) => (
                  <div key={queue.key} className={`rounded-[1.2rem] border p-4 ${toneClasses(queue.tone)}`}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em]">{queue.label}</p>
                    <p className="mt-3 text-2xl font-black text-white">{formatNumber(queue.value || 0)}</p>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Recent activity">
              <div className="space-y-3">
                {(snapshot?.recentActivity || []).length ? (
                  snapshot.recentActivity.map((item) => (
                    <div key={item.id} className="rounded-[1.2rem] border border-[#4e4632] bg-[#111111] p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-bold text-white">{item.label}</p>
                          <p className="mt-2 text-sm leading-6 text-[#d2c5ab]">{item.description}</p>
                        </div>
                        <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${toneClasses(item.status)}`}>
                          {item.status || 'info'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <StatePanel message="No recent product activity is available yet." />
                )}
              </div>
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel title="Quick actions">
              <div className="space-y-3">
                {quickActions.length ? (
                  quickActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      onClick={() => runAction(action)}
                      disabled={busyAction === action.key}
                      className="flex w-full items-center justify-between gap-4 rounded-[1.2rem] border border-[#4e4632] bg-[#111111] px-4 py-4 text-left transition hover:border-[#f1c100]/35"
                    >
                      <div>
                        <p className="font-bold text-white">{action.label}</p>
                        <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-[#9a9078]">
                          {action.riskLevel || 'low'} risk
                        </p>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#f1c100]">
                        {busyAction === action.key ? 'Working...' : 'Run'}
                      </span>
                    </button>
                  ))
                ) : (
                  <StatePanel message="No quick actions are registered for this product desk yet." />
                )}
              </div>
            </Panel>

            <Panel title="Renewals">
              <div className="space-y-3">
                {(snapshot?.renewals || []).length ? (
                  snapshot.renewals.map((item) => (
                    <div key={item.key} className="rounded-[1.2rem] border border-[#4e4632] bg-[#111111] p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-bold text-white">{item.label}</p>
                          <p className="mt-2 text-sm text-[#d2c5ab]">
                            {item.daysLeft != null ? `${item.daysLeft} day${item.daysLeft === 1 ? '' : 's'} left` : 'No date'}
                          </p>
                        </div>
                        {item.actionUrl ? (
                          <a
                            href={item.actionUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full border border-[#f1c100]/25 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#f7d88a] transition hover:bg-[#f1c100] hover:text-[#241a00]"
                          >
                            Open source
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <StatePanel message="No renewal items are available for this product." />
                )}
              </div>
            </Panel>

            <Panel title="Command alerts">
              <div className="space-y-3">
                {alerts.length ? (
                  alerts.map((alert) => (
                    <div key={alert._id} className={`rounded-[1.2rem] border p-4 ${toneClasses(alert.severity)}`}>
                      <p className="font-bold text-white">{alert.title}</p>
                      <p className="mt-2 text-sm leading-6">{alert.message}</p>
                    </div>
                  ))
                ) : (
                  <StatePanel message="No open command alerts for this product right now." />
                )}
              </div>
            </Panel>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded-[1.8rem] border border-[#4e4632] bg-[#090909] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.28)]">
      <h2 className="text-xl font-black tracking-[-0.03em] text-white">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function SummaryCard({ label, value, accent = 'text-white' }) {
  return (
    <div className="rounded-[1.4rem] border border-[#4e4632] bg-[#111111] p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9a9078]">{label}</p>
      <p className={`mt-3 text-3xl font-black tracking-[-0.03em] ${accent}`}>{value}</p>
    </div>
  );
}

function StatePanel({ message, tone = 'default' }) {
  const classes =
    tone === 'error'
      ? 'border-[#93000a]/60 bg-[#2a1112] text-[#ffdad6]'
      : tone === 'success'
        ? 'border-[#f1c100]/20 bg-[#14120a] text-[#f7d88a]'
        : 'border-[#4e4632] bg-[#111111] text-[#d2c5ab]';
  return <div className={`rounded-[1.4rem] border p-4 text-sm ${classes}`}>{message}</div>;
}
